import ErrorMessage from "@/v2-ui/ErrorMessage"
import ViewSheet, {
  type ViewSheetProps,
} from "@/v2-backoffice-client/components/viewSheet"
import Tabs from "@/v2-backoffice-client/components/tabs"
import { useUptimeConfigQuery } from "@/v2-backoffice-client/uptime/config/uptime.config.query"
import UptimeConfigPlatformsTab from "@/v2-backoffice-client/uptime/configSheet/UptimeConfigPlatformsTab"
import UptimeConfigCategoriesTab from "@/v2-backoffice-client/uptime/configSheet/UptimeConfigCategoriesTab"
import UptimeConfigProvidersTab from "@/v2-backoffice-client/uptime/configSheet/UptimeConfigProvidersTab"
import UptimeConfigProbesTab from "@/v2-backoffice-client/uptime/configSheet/UptimeConfigProbesTab"

export type UptimeConfigSheetProps = Omit<ViewSheetProps, "id" | "children">

// UptimeConfigSheet is the operator entry point for editing the
// editorial status configuration shared with the public status
// page. Mounted via a `?config=1` toggle on UptimePage; each tab
// is a small list editor backed by a single mutation per write.
//
// We sit beside the AppViewSheet rather than reusing it because
// the existing kind:id URL convention assumes one entity per
// sheet -- this sheet has no entity, just nested editors. Using a
// dedicated query param also keeps deep-links to the per-target
// uptime sheet (`?v=uptime:...`) composable with "open the config
// at the same time".
//
// The sheet always loads the entire config in one round-trip from
// `getStatusConfig` (a tiny payload, ~kB-scale even with hundreds
// of probes) so tab switches stay instant and the Categories tab
// can cross-reference platforms / sources / providers without
// refetching.
function UptimeConfigSheet(props: UptimeConfigSheetProps) {
  const { ...sheetProps } = props

  const { data, loading, error } = useUptimeConfigQuery({
    skip: !sheetProps.open,
  })
  const config = data?.getStatusConfig

  return (
    <ViewSheet id="uptime-config-sheet" {...sheetProps}>
      <ViewSheet.Header>
        <ViewSheet.Title>Uptime configuration</ViewSheet.Title>
      </ViewSheet.Header>

      {error && (
        <div className="p-4">
          <ErrorMessage error={error} />
        </div>
      )}

      <Tabs
        id="uptime-config-tabs"
        defaultValue="platforms"
        emptyLabel="Configuration unavailable."
        isLoading={loading && !config}
        error={error}
        isEmpty={!config}
      >
        <Tabs.List>
          <Tabs.Trigger value="platforms" count={config?.Platforms.length}>
            Platforms
          </Tabs.Trigger>
          <Tabs.Trigger value="categories" count={config?.Categories.length}>
            Categories
          </Tabs.Trigger>
          <Tabs.Trigger value="providers" count={config?.Providers.length}>
            Providers
          </Tabs.Trigger>
          <Tabs.Trigger value="probes">Probes</Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="platforms" className="flex-1 p-3">
          {config && <UptimeConfigPlatformsTab platforms={config.Platforms} />}
        </Tabs.Content>

        <Tabs.Content value="categories" className="flex-1 p-3">
          {config && (
            <UptimeConfigCategoriesTab
              platforms={config.Platforms}
              categories={config.Categories}
            />
          )}
        </Tabs.Content>

        <Tabs.Content value="providers" className="flex-1 p-3">
          {config && <UptimeConfigProvidersTab providers={config.Providers} />}
        </Tabs.Content>

        <Tabs.Content value="probes" className="flex-1 min-h-0">
          <UptimeConfigProbesTab />
        </Tabs.Content>
      </Tabs>
    </ViewSheet>
  )
}

export default UptimeConfigSheet
